import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { ReactiveFormsModule, FormControl, FormGroup } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';

@Component({
  selector: 'anihis-fna-finding',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    TranslocoModule,
  ],
  templateUrl: './fna-finding.component.html',
  styleUrls: ['./fna-finding.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FnaFindingComponent implements OnInit, OnDestroy {
  @Output() formChange = new EventEmitter<any>();
  form: FormGroup = new FormGroup({
    rtgInterpretation: new FormGroup({
      en: new FormControl(''),
      sr: new FormControl(''),
    }),
    fnaInterpretation: new FormGroup({
      en: new FormControl(''),
      sr: new FormControl(''),
    }),
    fnaDiagnosis: new FormGroup({
      en: new FormControl(''),
      sr: new FormControl(''),
    }),
    therapy: new FormGroup({
      en: new FormControl(''),
      sr: new FormControl(''),
    }),
    note: new FormGroup({
      en: new FormControl(''),
      sr: new FormControl(''),
    }),
    veterinarianName: new FormControl(''),
  });

  private subs: Subscription[] = [];

  private translocoService = inject(TranslocoService);
  currentLang: 'en' | 'sr' = 'en';

  ngOnInit(): void {
    const lang =
      (this.translocoService.getActiveLang &&
        this.translocoService.getActiveLang()) ||
      '';
    this.currentLang =
      typeof lang === 'string' && lang.startsWith('sr') ? 'sr' : 'en';

    // emit initial value
    this.formChange.emit(this.form.value);

    this.subscribeToVisibleControls();

    if ((this.translocoService as any).langChanges$) {
      const langSub = (this.translocoService as any).langChanges$.subscribe(
        () => {
          const newLang =
            (this.translocoService.getActiveLang &&
              this.translocoService.getActiveLang()) ||
            '';
          const target: 'en' | 'sr' =
            typeof newLang === 'string' && newLang.startsWith('sr')
              ? 'sr'
              : 'en';
          if (target !== this.currentLang) {
            this.currentLang = target;
            this.unsubscribeAll();
            this.subscribeToVisibleControls();
          }
        },
      );
      this.subs.push(langSub);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeAll();
  }

  private unsubscribeAll() {
    this.subs.forEach((s) => s.unsubscribe());
    this.subs = [];
  }

  private subscribeToVisibleControls() {
    const fields = [
      'rtgInterpretation',
      'fnaInterpretation',
      'fnaDiagnosis',
      'therapy',
      'note',
    ];
    fields.forEach((field) => {
      const visibleControl = this.form.get([field, this.currentLang]);
      const otherControl = this.form.get([
        field,
        this.currentLang === 'en' ? 'sr' : 'en',
      ]);
      if (visibleControl) {
        const s = (visibleControl as any).valueChanges.subscribe((val: any) => {
          if (otherControl) {
            otherControl.setValue(val, { emitEvent: false });
          }
          this.formChange.emit(this.form.value);
        });
        this.subs.push(s);
      }
    });

    const vetNameControl = this.form.get('veterinarianName');
    if (vetNameControl) {
      const s = vetNameControl.valueChanges.subscribe(() => {
        this.formChange.emit(this.form.value);
      });
      this.subs.push(s);
    }
  }
}
